import { notification } from 'antd';
import { useState } from 'react';

import { useMsal } from '@azure/msal-react';

import {
	BookedForSalesEventSummary,
	Customer,
	CustomerFacility,
	CustomerFilter,
	CustomerSummary,
	SalesGroupDto,
} from '../../../api-client';
import {
	AdminApiClient,
	BookedForSalesApiClient,
	CustomerApiClient,
} from '../../../providers/api-provider';

import { filterCustomersBySearch } from '../../../utils/customer-utils';
import { validateCustomer } from '../../../utils/axios-helpers/request-validation-helper';

export const useCustomers = () => {
	const { instance } = useMsal();

	const [customersSummary, setCustomersSummary] = useState<CustomerSummary[]>();
	const [customersSummaryLoading, setCustomersSummaryLoading] =
		useState<boolean>(false);

	const handleCustomersSummary = async (
		salesAgentId: number,
		customerFilter: CustomerFilter,
		salesGroup: string | undefined,
	) => {
		setCustomersSummaryLoading(true);
		setCustomersSummaryByFiltersLoading(true);

		if (!salesGroup) {
			const clientApi = await CustomerApiClient(instance);
			clientApi
				.getCustomersSummaryBySalesAgent(salesAgentId, customerFilter)
				.then((response) => {
					setCustomersSummary(response.data);
					setCustomersSummaryByFilters(response.data);
				})
				.catch((reason) => {
					setCustomersSummary([]);
					setCustomersSummaryByFilters([]);

					notification.error({
						message: 'Unable to retrieve customers!',
						description: 'Unable to retrieve customers summary.',
						duration: 2,
					});
				})
				.finally(() => {
					setCustomersSummaryLoading(false);
					setCustomersSummaryByFiltersLoading(false);
				});
		} else {
			const adminApi = await AdminApiClient(instance);
			adminApi
				.getCustomersSummaryBySalesGroup(salesGroup, customerFilter)
				.then((response) => {
					setCustomersSummary(response.data);
					setCustomersSummaryByFilters(response.data);
				})
				.catch((reason) => {
					setCustomersSummary([]);
					setCustomersSummaryByFilters([]);

					notification.error({
						message: 'Unable to retrieve customers!',
						description: 'Unable to retrieve customers summary.',
						duration: 2,
					});
				})
				.finally(() => {
					setCustomersSummaryLoading(false);
					setCustomersSummaryByFiltersLoading(false);
				});
		}
	};

	const [customersSummaryByFilters, setCustomersSummaryByFilters] =
		useState<CustomerSummary[]>();
	const [
		customersSummaryByFiltersLoading,
		setCustomersSummaryByFiltersLoading,
	] = useState<boolean>(false);

	const handleCustomersSummaryByFilters = async (dataFilter: any) => {
		setCustomersSummaryByFiltersLoading(true);

		setCustomersSummaryByFilters(
			filterCustomersBySearch(customersSummary, dataFilter?.BV_SEARCH_Customer),
		);

		setCustomersSummaryByFiltersLoading(false);
	};

	const [customer, setCustomer] = useState<Customer>();
	const [customerLoading, setCustomerLoading] = useState<boolean>(false);

	const handleCustomer = async (id: number = -1) => {
		setCustomerLoading(true);

		validateCustomer(id);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomer(id)
			.then((response) => {
				setCustomer(response.data);
			})
			.catch((reason) => {
				setCustomer({});

				notification.error({
					message: 'Unable to retrieve customer details!',
					description: 'Unable to retrieve customer details.',
					duration: 2,
				});
			})
			.finally(() => {
				setCustomerLoading(false);
			});
	};

	const [facilities, setFacilities] = useState<CustomerFacility[]>();
	const [facilitiesLoading, setFacilitiesLoading] = useState<boolean>(false);

	const handleFacilities = async (id: number = -1) => {
		setFacilitiesLoading(true);

		validateCustomer(id);

		const clientApi = await CustomerApiClient(instance);

		clientApi
			.getCustomerFacilities(id)
			.then((response) => {
				setFacilities(response.data);
			})
			.catch((reason) => {
				setFacilities([]);

				notification.error({
					message: 'Unable to retrieve customer facilities!',
					description: 'Unable to retrieve customer facilities.',
					duration: 2,
				});
			})
			.finally(() => {
				setFacilitiesLoading(false);
			});
	};

	const [events, setEvents] = useState<BookedForSalesEventSummary[]>([]);
	const [eventsLoading, setEventsLoading] = useState<boolean>(false);

	const handleEvents = async () => {
		setEventsLoading(true);

		const clientApi = await BookedForSalesApiClient(instance);

		clientApi
			.getBookedForSalesEvents()
			.then((response) => {
				setEvents(response.data);
			})
			.catch((reason) => {
				setEvents([]);

				notification.error({
					message: 'Unable to retrieve customer events!',
					description: 'Unable to retrieve customer events.',
					duration: 2,
				});
			})
			.finally(() => {
				setEventsLoading(false);
			});
	};

	const [orderReasons, setOrderReasons] = useState<any>();
	const [orderReasonsLoading, setOrderReasonsLoading] =
		useState<boolean>(false);

	const handleOrderReasons = async () => {
		setOrderReasonsLoading(true);

		const clientApi = await BookedForSalesApiClient(instance);

		clientApi
			.getBookedForSalesOrderReasons()
			.then((response) => {
				setOrderReasons(response.data);
			})
			.catch((reason) => {
				setOrderReasons([]);

				notification.error({
					message: 'Unable to retrieve customer order reasons!',
					description: 'Unable to retrieve customer order reasons.',
					duration: 2,
				});
			})
			.finally(() => {
				setOrderReasonsLoading(false);
			});
	};

	// =========================
	// Admin area
	// =========================

	const [salesGroups, setSalesGroups] = useState<SalesGroupDto[]>();
	const [salesGroupsLoading, setSalesGroupsLoading] = useState<boolean>(false);

	const handleSalesGroup = async () => {
		setSalesGroupsLoading(true);

		const adminApi = await AdminApiClient(instance);

		adminApi
			.getAllSalesGroups()
			.then((response) => {
				setSalesGroups(response.data);
			})
			.catch((reason) => {
				setSalesGroups([]);

				notification.error({
					message: 'Unable to retrieve customer order reasons!',
					description: 'Unable to retrieve customer order reasons.',
					duration: 2,
				});
			})
			.finally(() => {
				setSalesGroupsLoading(false);
			});
	};

	return {
		handleCustomersSummary,
		customersSummary,
		customersSummaryLoading,
		// FILTERS HANDLER
		handleCustomersSummaryByFilters,
		customersSummaryByFilters,
		customersSummaryByFiltersLoading,
		handleCustomer,
		customer,
		customerLoading,
		handleFacilities,
		facilities,
		facilitiesLoading,
		handleEvents,
		events,
		eventsLoading,
		handleOrderReasons,
		orderReasons,
		orderReasonsLoading,
		//ADMIN
		salesGroups,
		salesGroupsLoading,
		handleSalesGroup,
	};
};
